import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, MapPin } from "lucide-react";
import { longDate, usd } from "@/lib/training";
import { FACILITY, FACILITY_ADDRESS, DIRECTIONS_URL } from "@/lib/location";
import LessonDatePicker from "./LessonDatePicker";
import RegistrationFlow from "./RegistrationFlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { hitting: "Hitting", pitching: "Pitching" };
const Head = ({ n, t }) => <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/40"><span className="text-white">{n}</span> &nbsp;{t}</p>;

const endLabel = (iso) => new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });

export default function LessonsPage() {
  const [params] = useSearchParams();
  const [types, setTypes] = useState(null);
  const [policy, setPolicy] = useState("");
  const [type, setType] = useState(null);
  const [disc, setDisc] = useState("");
  const [horizon, setHorizon] = useState(30);
  const [slots, setSlots] = useState(null);
  const [day, setDay] = useState("");
  const [reload, setReload] = useState(0);
  const [taken, setTaken] = useState("");
  const [slot, setSlot] = useState(null);
  const [registering, setRegistering] = useState(false);

  useEffect(() => { axios.get(`${API}/lessons/types`).then((r) => { const t = r.data.types; setTypes(t); setPolicy(r.data.cancellation_policy); setHorizon(r.data.horizon_days || 30); const ds = [...new Set(t.map((x) => x.discipline))]; if (ds.length === 1) setDisc(ds[0]); }); }, []);
  const discs = useMemo(() => ["hitting", "pitching"].filter((d) => (types || []).some((t) => t.discipline === d)), [types]);
  const lengths = useMemo(() => (types || []).filter((t) => t.discipline === disc).sort((a, b) => a.duration_minutes - b.duration_minutes), [types, disc]);
  useEffect(() => { setType(lengths.length === 1 ? lengths[0] : null); }, [lengths]);
  useEffect(() => { setDay(""); setTaken(""); }, [type]);
  useEffect(() => {
    if (!type) return;
    setSlots(null); setSlot(null); setRegistering(false);
    axios.get(`${API}/lessons/slots`, { params: { type_id: type.id, days: horizon } }).then((r) => setSlots(r.data.slots));
  }, [type, horizon, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (params.get("checkout") === "cancelled" && params.get("rid")) axios.post(`${API}/lessons/release`, { registration_id: params.get("rid") }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const days = useMemo(() => [...new Set((slots || []).map((s) => s.date))], [slots]);
  const offering = slot && { id: "lesson", lesson: { lesson_type_id: type.id, start: slot.start }, price: type.price, age_group: "", title: type.name };

  return (
    <div className="min-h-[100svh] bg-[#0B0F19] text-white">
      <header className="sticky top-0 z-40 bg-[#0B0F19]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center"><Link to="/#training" data-testid="lessons-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft size={16} /> All training</Link></div>
      </header>
      <main className="max-w-3xl mx-auto px-5 pb-24">
        <h1 data-testid="lessons-title" className="mt-8 text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase leading-[0.92]">Private Lessons</h1>
        <p className="mt-4 text-base font-medium text-white/60">One-on-one coaching. Pick a lesson, a day, and a time — payment confirms your spot.</p>
        <p className="mt-3 flex flex-wrap gap-x-3 text-sm font-semibold text-white/70"><span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-white/45" /> {FACILITY.name} · {FACILITY_ADDRESS}</span><a href={DIRECTIONS_URL} target="_blank" rel="noreferrer" className="font-bold text-[#60a5fa]">Get Directions</a></p>
        {params.get("checkout") === "cancelled" && <p data-testid="lesson-cancelled-note" className="mt-5 rounded-2xl border border-white/15 px-4 py-3 text-sm font-semibold text-white/70">Checkout was canceled — you weren't charged and the time was released.</p>}
        {!types && <Loader2 className="mt-12 animate-spin text-white/40" />}
        {types?.length === 0 && <p data-testid="lessons-empty" className="mt-12 rounded-3xl border border-white/10 p-8 text-lg font-bold">Private lesson booking opens soon. Email <a className="underline" href="mailto:info@replabbaseball.com">info@replabbaseball.com</a>.</p>}
        {types?.length > 0 && (
          <div className="mt-10 space-y-10">
            <div data-testid="lesson-step-discipline">
              <Head n="1" t="Hitting or pitching" />
              <div className="mt-4 grid grid-cols-2 gap-3">
                {discs.map((d) => (
                  <button key={d} data-testid={`lesson-discipline-${d}`} onClick={() => setDisc(d)} className={`text-left rounded-2xl border-2 p-5 transition-colors ${disc === d ? "border-white bg-white/[0.06]" : "border-white/10 hover:border-white/30"}`}>
                    <span className="block text-lg font-extrabold">{LABEL[d]} Lessons</span>
                    <span className="block text-sm font-medium text-white/55">From {usd(Math.min(...types.filter((t) => t.discipline === d).map((t) => t.price)))}</span>
                  </button>
                ))}
              </div>
            </div>
            {disc && (
              <div data-testid="lesson-step-length">
                <Head n="2" t="Lesson length" />
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {lengths.map((t) => (
                    <button key={t.id} data-testid={`lesson-type-${t.id}`} onClick={() => setType(t)} className={`text-left rounded-2xl border-2 p-5 transition-colors ${type?.id === t.id ? "border-white bg-white/[0.06]" : "border-white/10 hover:border-white/30"}`}>
                      <span className="block text-lg font-extrabold">{t.duration_minutes} minutes</span>
                      <span className="block text-sm font-medium text-white/55">{t.name}</span>
                      <span className="mt-2 block text-2xl font-extrabold">{usd(t.price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {type && (
              <div data-testid="lesson-step-day">
                <Head n="3" t="Choose a day" />
                {!slots && <Loader2 className="mt-4 animate-spin text-white/40" />}
                {slots?.length === 0 && <p data-testid="lesson-no-slots" className="mt-4 text-sm font-semibold text-white/60">No open times right now. Email info@replabbaseball.com and we'll find one.</p>}
                {slots?.length > 0 && <LessonDatePicker key={type.id} days={days} value={day} onPick={(d) => { setDay(d); setSlot(null); setRegistering(false); }} />}
              </div>
            )}
            {day && (
              <div data-testid="lesson-step-time">
                {taken && <p data-testid="lesson-taken-msg" className="mb-3 rounded-2xl border border-amber-300/40 px-4 py-3 text-sm font-semibold text-amber-200">{taken}</p>}
                <Head n="4" t={`Choose a time · ${longDate(day)} (Central)`} />
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {!slots.some((s) => s.date === day) && <p data-testid="lesson-no-times" className="col-span-full text-sm font-semibold text-white/60">No times left on this date. Please pick another date.</p>}
                  {slots.filter((s) => s.date === day).map((s) => <button key={s.start} data-testid={`lesson-slot-${s.label.replace(/[^0-9APM]/g, "")}`} onClick={() => { setSlot(s); setRegistering(false); setTaken(""); }} className={`rounded-xl py-3.5 text-sm font-bold ${slot?.start === s.start ? "bg-white text-[#0B0F19]" : "border border-white/15 text-white/80"}`}>{s.label}</button>)}
                </div>
              </div>
            )}
            {slot && (
              <div data-testid="lesson-summary" className="rounded-3xl bg-white text-[#0B0F19] p-5 md:p-7">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Lesson summary</p>
                <p className="mt-2 text-lg font-extrabold">{type.name}</p>
                <p data-testid="lesson-summary-time" className="text-sm font-semibold text-slate-600">{longDate(slot.date)} · {slot.label}–{endLabel(slot.end)} Central · {type.duration_minutes} min</p>
                <p className="text-sm font-semibold text-slate-600">{FACILITY.name}, {FACILITY_ADDRESS}</p>
                {policy && <p className="mt-3 text-xs font-medium text-slate-500">{policy}</p>}
                <div className="mt-4 flex items-end justify-between gap-4">
                  <p data-testid="lesson-total" className="text-3xl font-extrabold tracking-tighter">{usd(type.price)}</p>
                  {!registering && <button data-testid="lesson-book-btn" onClick={() => setRegistering(true)} className="inline-flex items-center gap-2 rounded-full bg-[#0B0F19] hover:bg-[#2563EB] text-white font-bold px-7 py-4">Book Lesson <ArrowRight size={18} /></button>}
                </div>
              </div>
            )}
            {registering && offering && <RegistrationFlow key={slot.start} offering={offering} onConflict={(m) => { setTaken(m || "That time was just taken. Please choose another time."); setRegistering(false); setSlot(null); setReload((n) => n + 1); }} />}
          </div>
        )}
      </main>
    </div>
  );
}
